import { useEffect, useRef, useState } from "react";
import { supabase } from "../supabase";
import { escapeLikePattern } from "../utils/likePattern";

const MAX_SUGGESTIONS = 6;
const MENTION_DEBOUNCE_MS = 250;

function getCurrentWord(text, cursor) {
  const upto = text.slice(0, cursor);
  const start = Math.max(upto.lastIndexOf(" "), upto.lastIndexOf("\n")) + 1;
  return { start, word: text.slice(start, cursor) };
}

export default function HashtagInput({
  value,
  onChange,
  placeholder,
  disabled,
  multiline,
  maxLength,
  rows,
  autoFocus,
  inputStyle,
}) {
  const [allTags, setAllTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]); // hashtag matches
  const [mentionSuggestions, setMentionSuggestions] = useState([]); // username matches
  const [activeIndex, setActiveIndex] = useState(-1); // keyboard-highlighted suggestion
  const inputRef = useRef(null);
  const mentionRequestId = useRef(0);
  const mentionTimeout = useRef(null);

  // Build a "most used first" list of hashtags from recent posts, for autocomplete
  useEffect(() => {
    async function loadTags() {
      const { data, error } = await supabase
        .from("designs")
        .select("hashtags")
        .not("hashtags", "is", null)
        .order("created_at", { ascending: false })
        .limit(300);

      if (error) {
        console.log("HASHTAG SUGGESTIONS ERROR:", error);
        return;
      }

      const frequency = {};
      (data || []).forEach(row => {
        (row.hashtags || []).forEach(tag => {
          frequency[tag] = (frequency[tag] || 0) + 1;
        });
      });

      const sorted = Object.keys(frequency).sort(
        (a, b) => frequency[b] - frequency[a] || a.localeCompare(b)
      );
      setAllTags(sorted);
    }

    loadTags();
  }, []);

  // Clear any pending debounced mention lookup on unmount.
  useEffect(() => {
    return () => {
      if (mentionTimeout.current) clearTimeout(mentionTimeout.current);
    };
  }, []);

  // Usernames aren't preloaded like hashtags (that list would grow
  // unbounded) — instead debounce a small indexed prefix query per keystroke.
  function searchMentions(prefix) {
    const requestId = ++mentionRequestId.current;

    if (mentionTimeout.current) clearTimeout(mentionTimeout.current);

    mentionTimeout.current = setTimeout(async () => {
      const { data, error } = await supabase
        .from("users")
        .select("username, avatar_url")
        .ilike("username", `${escapeLikePattern(prefix)}%`)
        .order("username")
        .limit(MAX_SUGGESTIONS);

      // A newer keystroke may have started another lookup while this one
      // was in flight — ignore a response that's no longer the latest.
      if (requestId !== mentionRequestId.current) return;

      if (error) {
        console.log("MENTION SUGGESTIONS ERROR:", error);
        return;
      }

      setMentionSuggestions(data || []);
    }, MENTION_DEBOUNCE_MS);
  }

  // Leaving "@" mode without this left the debounced lookup armed — typing
  // "@ab" then immediately "#tag" cleared mentionSuggestions right away, but
  // the still-pending timeout from "@ab" fired ~250ms later and set it right
  // back, popping the mention dropdown back open after the user had already
  // moved on to a hashtag.
  function cancelPendingMentionSearch() {
    if (mentionTimeout.current) {
      clearTimeout(mentionTimeout.current);
      mentionTimeout.current = null;
    }
    mentionRequestId.current++;
  }

  function handleChange(e) {
    const text = e.target.value;
    onChange(text);

    const cursor = e.target.selectionStart;
    const { word } = getCurrentWord(text, cursor);

    setActiveIndex(-1);

    if (word.startsWith("#") && word.length > 1) {
      const prefix = word.slice(1).toLowerCase();
      const matches = allTags
        .filter(tag => tag.startsWith(prefix) && tag !== prefix)
        .slice(0, MAX_SUGGESTIONS);
      setSuggestions(matches);
      cancelPendingMentionSearch();
      setMentionSuggestions([]);
    } else if (word.startsWith("@") && word.length > 1) {
      setSuggestions([]);
      searchMentions(word.slice(1).toLowerCase());
    } else {
      setSuggestions([]);
      cancelPendingMentionSearch();
      setMentionSuggestions([]);
    }
  }

  // Suggestions were mouse-only — a keyboard user typing "#phul" or "@aman"
  // got a visible dropdown with no way to select from it (Tab just left the
  // field). Arrow keys move a highlighted option, Enter picks it, Escape
  // dismisses — the same pattern TruncatedText/PickerSelect got in an
  // earlier pass for the same class of gap.
  function handleKeyDown(e) {
    const isMentionMode = mentionSuggestions.length > 0;
    const activeSuggestions = isMentionMode ? mentionSuggestions : suggestions;
    if (activeSuggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % activeSuggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? activeSuggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      const pick = activeSuggestions[activeIndex] ?? activeSuggestions[0];
      if (!pick) return;
      e.preventDefault();
      if (isMentionMode) applyMention(pick.username);
      else applySuggestion(pick);
    } else if (e.key === "Escape") {
      e.preventDefault();
      // Without this, dismissing just this dropdown also bubbles up to a
      // window-level Escape handler a caller might have (Comments.jsx's
      // sheet closes on Escape) — preventDefault alone only suppresses the
      // browser's own default action, not propagation.
      e.stopPropagation();
      setSuggestions([]);
      cancelPendingMentionSearch();
      setMentionSuggestions([]);
    }
  }

  function applySuggestion(tag) {
    const input = inputRef.current;
    const cursor = input ? input.selectionStart : value.length;
    const { start } = getCurrentWord(value, cursor);
    const before = value.slice(0, start);
    const after = value.slice(cursor);
    // onChange sets the value directly — the field's maxLength attribute
    // only clamps native typing, not a value assigned from here, so a long
    // suggestion picked near the limit could otherwise push past it.
    let newText = `${before}#${tag} ${after}`;
    if (maxLength) newText = newText.slice(0, maxLength);

    onChange(newText);
    setSuggestions([]);

    requestAnimationFrame(() => {
      if (!input) return;
      const pos = Math.min(before.length + tag.length + 2, newText.length);
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  }

  function applyMention(username) {
    const input = inputRef.current;
    const cursor = input ? input.selectionStart : value.length;
    const { start } = getCurrentWord(value, cursor);
    const before = value.slice(0, start);
    const after = value.slice(cursor);
    let newText = `${before}@${username} ${after}`;
    if (maxLength) newText = newText.slice(0, maxLength);

    onChange(newText);
    setMentionSuggestions([]);

    requestAnimationFrame(() => {
      if (!input) return;
      const pos = Math.min(before.length + username.length + 2, newText.length);
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  }

  const Field = multiline ? "textarea" : "input";
  const isMentionMode = mentionSuggestions.length > 0;
  const activeSuggestions = isMentionMode ? mentionSuggestions : suggestions;
  const activeId = activeIndex >= 0 && activeIndex < activeSuggestions.length ? `hashtag-suggestion-${activeIndex}` : undefined;

  return (
    <div style={wrap}>
      <Field
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={multiline ? rows || 4 : undefined}
        autoFocus={autoFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={activeSuggestions.length > 0}
        aria-haspopup="listbox"
        aria-controls={activeSuggestions.length > 0 ? "hashtag-suggestion-list" : undefined}
        aria-activedescendant={activeId}
        onBlur={() => setTimeout(() => {
          setSuggestions([]);
          setMentionSuggestions([]);
        }, 150)}
        style={multiline ? { ...input, resize: "vertical", minHeight: 90, ...inputStyle } : { ...input, ...inputStyle }}
      />

      {activeSuggestions.length > 0 && (
        <div style={dropdown} role="listbox" id="hashtag-suggestion-list">
          {activeSuggestions.map((item, i) => {
            const key = isMentionMode ? item.username : item;
            const isActive = i === activeIndex;
            return (
              <div
                key={key}
                id={`hashtag-suggestion-${i}`}
                role="option"
                aria-selected={isActive}
                style={{
                  ...dropdownItem,
                  ...(isActive ? dropdownItemActive : {}),
                  borderBottom: i === activeSuggestions.length - 1 ? "none" : "1px solid var(--border-soft)",
                }}
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => (isMentionMode ? applyMention(item.username) : applySuggestion(item))}
              >
                {isMentionMode ? (
                  <span style={mentionRow}>
                    <img
                      src={item.avatar_url || "/default-avatar.svg"}
                      alt=""
                      style={mentionAvatar}
                    />
                    @{item.username}
                  </span>
                ) : (
                  `#${item}`
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ===== STYLES (Virsa palette) ===== */

const wrap = { position: "relative" };

const input = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const dropdown = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-input)",
  borderRadius: 10,
  boxShadow: "0 8px 20px rgba(59,10,20,0.15)",
  overflow: "hidden",
  zIndex: 20,
};

const dropdownItem = {
  padding: "9px 14px",
  fontSize: 13.5,
  color: "var(--accent-primary)",
  cursor: "pointer",
  borderBottom: "1px solid var(--border-soft)",
};

const dropdownItemActive = {
  background: "var(--bg-soft)",
};

const mentionRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const mentionAvatar = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
};
